import * as React from 'react';
import { observer } from 'mobx-react-lite';
import type { SearchResultItem } from 'owa-timestream-search-orchestration';
import type CalendarItem from 'owa-service/lib/contract/CalendarItem';
import CalendarSearchIcon from './CalendarSearchIcon';
import formatMeetingTimeSpan from 'owa-timeformat/lib/utils/formatMeetingTimeSpan';
import { userDate } from 'owa-datetime';
import { lazyPopoutCalendarReadingPane } from 'owa-popout-calendar';
import CalendarSearchHeader from './CalendarSearchHeader';
import RSVPStatus from './RSVPStatus';
import { getCalendarEventItemTitleWithLocation } from 'owa-calendar-event-attributes';
import { calendarEvent } from 'owa-calendar-event-converter';
import { getUserMailboxInfo } from 'owa-client-ids';
import { SearchResultOptions } from 'owa-timestream-search-view';
import { useInnerFocusTracker } from 'owa-react-hooks/lib/useInnerFocusTracker';
import { useBooleanState } from 'owa-react-hooks/lib/useBooleanState';
import classNames from 'classnames';

import * as styles from './styles.scss';

export interface CalendarSearchResultProps {
    item: SearchResultItem;
}

export default observer(function CalendarSearchResult(props: CalendarSearchResultProps) {
    const { item } = props;
    const calendarItem = item.data as CalendarItem;
    const userMailboxInfo = getUserMailboxInfo();

    const ref = React.useRef<HTMLDivElement>(null);
    const { hasFocusState, onFocusCapture, onBlurCapture } = useInnerFocusTracker(ref);
    const [optionsOpen, setOptionsOpen, resetOptionsOpen] = useBooleanState(false);

    const popoutPane = React.useCallback(() => {
        const itemId = calendarItem.ItemId?.Id;
        if (itemId) {
            lazyPopoutCalendarReadingPane.importAndExecute(
                {
                    Id: itemId,
                    mailboxInfo: userMailboxInfo,
                },
                'Spaces'
            );
        }
    }, [calendarItem.ItemId]);

    if (item.id.indexOf('calendarItem_monthHeader') >= 0) {
        return <CalendarSearchHeader item={item} />;
    }

    const parsedStart = userDate(calendarItem.Start || '');
    const parsedEnd = userDate(calendarItem.End || '');
    const meetingString = formatMeetingTimeSpan(parsedStart, parsedEnd);

    const details = getCalendarEventItemTitleWithLocation(
        calendarEvent(calendarItem, userMailboxInfo)
    );

    return (
        <div
            ref={ref}
            className={styles.searchResult}
            tabIndex={0}
            data-is-focusable={true}
            onFocusCapture={onFocusCapture}
            onBlurCapture={onBlurCapture}>
            <div className={styles.personaIconWrapper}>
                <CalendarSearchIcon />
            </div>
            <div className={styles.searchResultContent} title={details}>
                <div className={styles.searchResultSender}>
                    {calendarItem.Organizer?.Mailbox?.Name}
                </div>
                <div className={styles.searchResultTitle}>{calendarItem.Subject}</div>
                <div className={styles.searchResultCalendarDate}>{meetingString}</div>
                <div className={styles.searchResultCalendarLocation}>
                    {calendarItem.Location?.DisplayName}
                </div>
                <RSVPStatus item={calendarItem} />
            </div>
            <div
                className={classNames(styles.searchResultOptions, {
                    [styles.optionsOpen]: optionsOpen,
                })}>
                <SearchResultOptions
                    isFocusable={hasFocusState}
                    assetRef={ref}
                    searchResultItem={item}
                    assetType={'calendarItem'}
                    onOpen={popoutPane}
                    onOptionsOpened={setOptionsOpen}
                    onOptionsClosed={resetOptionsOpen}
                />
            </div>
        </div>
    );
});
